'use client';

import { useTranslations } from 'next-intl';
import { useState } from 'react';
import { AnimatedButton } from '@/components/ui/animated-button';
import { LinkForwardIconSolid } from '@/components/ui/icon/glyphs';
import { TOAST_IDS } from '@/config/toast-ids';
import { withDemoMode } from '@/lib/network';
import { useUserNetwork } from '@/lib/network/client';
import { ShareModal } from '@/lib/share';
export interface ShareGiftCtaProps {
  /** Permalink for the public gift page (`/gifts/<opaqueId>`). */
  permalinkUrl: string;
  /** Sanitised recipient handle (e.g. `@alice`). Used to compose share text. */
  recipientDisplay: string;
  /** Recipient platform — drives the share template wording. `'x'` adds
   *  "'s X handle … sign in with X" so the receiver knows what to do.
   *  Defaults to `'morfi'` when omitted. */
  recipientPlatform?: 'morfi' | 'x';
  /** Pre-formatted display amount (e.g. `$25.00` or `25 USDC`). */
  amountText: string;
  /**
   * Asset kind. When `'outcome_token'`, the share template embeds the outcome
   * label and market title so recipients see "I gifted you $10 worth of Yes
   * shares on 'Will BTC hit 100k?'" instead of just a dollar amount. Defaults
   * to `'usdc'` for back-compat.
   */
  assetKind?: 'usdc' | 'outcome_token';
  /**
   * Outcome label (e.g. `Yes`). Required when `assetKind === 'outcome_token'`
   * for the outcome-aware share text. When null/empty, falls back to the
   * USDC template even for outcome-token gifts.
   */
  outcomeLabel?: string | null;
  /**
   * Market title (e.g. `Will BTC hit 100k?`). Required alongside
   * `outcomeLabel` for the outcome-aware share text.
   */
  marketTitle?: string | null;
  /** GiftDeposit pubkey for analytics correlation. Optional. */
  depositPubkey?: string;
  /**
   * Compact layout fits inline on a row footer (smaller trigger).
   * Default = `false` (full-size trigger for the success modal).
   */
  compact?: boolean;
  /** Optional className passed to the trigger button. */
  className?: string;
}

/**
 * Share-trigger for a gift permalink (FR-041 / FR-044).
 *
 * Renders a single Share button. Clicking it opens the unified ShareModal
 * with all platform options. The modal handles intent URL building,
 * clipboard, native share, analytics, and toast — no share code outside
 * `@/lib/share`.
 */
export function ShareGiftCta(props: ShareGiftCtaProps): React.ReactNode {
  const t = useTranslations('gift.share');
  const {
    network
  } = useUserNetwork();
  const compact = props.compact === true;
  const sharePermalinkUrl = ((): string => {
    if (props.permalinkUrl.length === 0) return props.permalinkUrl;
    try {
      const u = new URL(props.permalinkUrl);
      withDemoMode(u, network === 'mainnet' ? 'mainnet' : 'devnet');
      return u.toString();
    } catch {
      // SILENT-CATCH: invalid URL — fall back to the unmodified permalink.
      return props.permalinkUrl;
    }
  })();
  const isOutcome = props.assetKind === 'outcome_token' && props.outcomeLabel !== null && props.outcomeLabel !== undefined && props.outcomeLabel.length > 0 && props.marketTitle !== null && props.marketTitle !== undefined && props.marketTitle.length > 0;
  const platform = props.recipientPlatform ?? 'morfi';
  const templateKey = isOutcome ? platform === 'x' ? 'shareText_x_outcome' : 'shareText_morfi_outcome' : platform === 'x' ? 'shareText_x' : 'shareText_morfi';
  const text = isOutcome ? t(templateKey, {
    recipientHandle: props.recipientDisplay,
    amount: props.amountText,
    outcomeLabel: props.outcomeLabel ?? '',
    marketTitle: props.marketTitle ?? ''
  }) : t(templateKey, {
    recipientHandle: props.recipientDisplay,
    amount: props.amountText
  });

  // Extract the opaque_id from `/gifts/<opaqueId>` (with or without locale
  // prefix). Used as the gift surface's `opaqueId` so OG URLs and the
  // permalink lookup line up with the page URL. `depositPubkey` is kept
  // separately for the `gift_share_clicked` analytics event.
  // Use immutable indexing (the array-mutating shorthand is banned by RC-001).
  const lastSegment = (() => {
    const trimmed = props.permalinkUrl.split('?')[0]?.split('#')[0] ?? '';
    const segments = trimmed.split('/');
    return segments[segments.length - 1];
  })();
  const opaqueId = lastSegment;
  const [shareOpen, setShareOpen] = useState<boolean>(false);
  return <>
      <AnimatedButton type="button" variant="secondary" size={compact ? 'sm' : 'default'} prefix={LinkForwardIconSolid} className={props.className} onClick={(): void => {
      setShareOpen(true);
    }}>
        {t('shareMore')}
      </AnimatedButton>
      <ShareModal open={shareOpen} onOpenChange={setShareOpen} surface={{
      kind: 'gift',
      ...(opaqueId !== undefined && opaqueId.length > 0 ? {
        opaqueId
      } : {}),
      ...(props.depositPubkey !== undefined ? {
        depositPubkey: props.depositPubkey
      } : {})
    }} url={sharePermalinkUrl} text={text} title={t('nativeShareTitle')} copyToast={{
      id: TOAST_IDS.GIFT_LINK_COPIED,
      message: t('copyLinkSuccess'),
      duration: 2000
    }} />
    </>;
}